/**
 * Navigation stack context for stacked navigation.
 *
 * NORTHSTARS:
 * - Less code, better
 * - React Router is boss - we react to URL, don't push history
 * - Trust the platform - localStorage for persistence
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { type TabId, TAB_ID_TO_PATH } from "@/routes/routeObjects";
import { restoreStack, serializeStack, STACK_STORAGE_KEY } from "@/components/navigation/stackPersistence";
import { recordStackLedger, recordStackRestore } from "@/lib/native/memoryLedger";

// Storage keys
const LAST_TAB_KEY = "stasht_last_tab";
const MAP_VIEW_KEY = "stasht_map_view";

// Map view state (migrated from TabNavigationContext)
export type MapViewState = {
  latitude: number;
  longitude: number;
  zoom: number;
};

// Context value type
export type StackEntry = {
  id: string;
  pathname: string;
  search: string;
  hash: string;
  state: unknown;
  /** history.state.idx of the location this entry was opened over; null when nothing is beneath it. */
  underIdx: number | null;
  /** Rebuilt from sessionStorage after a reload: mounts in place, no slide. */
  restored?: boolean;
};

export type TabLocation = {
  id: string;
  pathname: string;
  search: string;
  hash: string;
  state: unknown;
};

interface NavigationStackContextValue {
  // State (reactive - changes trigger re-renders)
  activeTab: TabId;
  stack: StackEntry[];
  lastActiveTab: TabId;
  tabLocations: Record<TabId, TabLocation>;

  // Actions
  setActiveTab: (tabId: TabId) => void;
  pushStack: (entry: StackEntry) => void;
  popStack: () => void;
  clearStack: () => void;
  setTabLocation: (tabId: TabId, location: TabLocation) => void;

  // Map view state (migrated from TabNavigationContext)
  setMapView: (view: MapViewState | null) => void;
  getMapView: () => MapViewState | null;

  // Reselect handlers
  registerReselect: (tabId: TabId, handler: () => void) => () => void;
  emitReselect: (tabId: TabId) => void;

  // Helpers
  getTabPath: (tabId: TabId) => string;
  getTabLocation: (tabId: TabId) => TabLocation;
}

const NavigationStackContext = createContext<NavigationStackContextValue | null>(null);

// LocalStorage helpers
function readLastTab(): TabId {
  if (typeof window === "undefined") return "home";
  const stored = localStorage.getItem(LAST_TAB_KEY);
  if (stored && stored in TAB_ID_TO_PATH) {
    return stored as TabId;
  }
  return "home";
}

function persistLastTab(tabId: TabId) {
  if (typeof window === "undefined") return;
  localStorage.setItem(LAST_TAB_KEY, tabId);
}

function readMapView(): MapViewState | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = localStorage.getItem(MAP_VIEW_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (
      typeof parsed?.latitude === "number" &&
      typeof parsed?.longitude === "number" &&
      typeof parsed?.zoom === "number"
    ) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

// The stack survives a reload (a deploy's chunk refresh, or the content process
// being replaced under memory pressure) when it still describes where the page is.
function readStoredStack(): StackEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const restored = restoreStack(sessionStorage.getItem(STACK_STORAGE_KEY)) ?? [];
    recordStackRestore({ href: window.location.pathname, restoredDepth: restored.length, restoredTop: restored[restored.length - 1]?.pathname ?? null });
    return restored;
  } catch {
    return [];
  }
}

function persistStack(stack: StackEntry[]) {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(STACK_STORAGE_KEY, serializeStack(stack));
  } catch {
    // Storage unavailable; the stack simply won't survive a reload.
  }
}

function persistMapView(view: MapViewState | null) {
  if (typeof window === "undefined") return;
  if (view) {
    localStorage.setItem(MAP_VIEW_KEY, JSON.stringify(view));
  } else {
    localStorage.removeItem(MAP_VIEW_KEY);
  }
}

const createDefaultTabLocations = (): Record<TabId, TabLocation> => {
  return (Object.entries(TAB_ID_TO_PATH) as Array<[TabId, string]>).reduce(
    (acc, [tabId, path]) => {
      acc[tabId] = {
        id: path,
        pathname: path,
        search: "",
        hash: "",
        state: null,
      };
      return acc;
    },
    {} as Record<TabId, TabLocation>,
  );
};

// Provider
export function NavigationStackProvider({ children }: { children: ReactNode }) {
  // Reactive state - changes trigger re-renders in consuming components
  const [activeTab, setActiveTabState] = useState<TabId>(readLastTab);
  const [stack, setStack] = useState<StackEntry[]>(readStoredStack);
  const [lastActiveTab, setLastActiveTab] = useState<TabId>(readLastTab);
  const [tabLocations, setTabLocations] = useState<Record<TabId, TabLocation>>(createDefaultTabLocations);

  // Non-reactive state (refs) - for values that don't need to trigger renders
  const mapViewRef = useRef<MapViewState | null>(readMapView());
  const reselectHandlersRef = useRef<Record<TabId, Set<() => void>>>({
    home: new Set(),
    stashes: new Set(),
    collections: new Set(),
    favorites: new Set(),
    done: new Set(),
    tags: new Set(),
    roundups: new Set(),
    notifications: new Set(),
    events: new Set(),
    map: new Set(),
    search: new Set(),
    profile: new Set(),
    discover: new Set(),
    explore: new Set(),
    chat: new Set(),
  });

  useEffect(() => {
    persistStack(stack);
    void recordStackLedger({
      route: stack[stack.length - 1]?.pathname ?? (typeof window === "undefined" ? "" : window.location.pathname),
      depth: stack.length,
    });
  }, [stack]);

  const setActiveTab = useCallback((tabId: TabId) => {
    setActiveTabState(tabId);
    setLastActiveTab(tabId);
    persistLastTab(tabId);
  }, []);

  const pushStack = useCallback((entry: StackEntry) => {
    setStack((current) => {
      // Don't push duplicates
      if (current[current.length - 1]?.id === entry.id) {
        return current;
      }
      return [...current, entry];
    });
  }, []);

  const popStack = useCallback(() => {
    setStack((current) => current.slice(0, -1));
  }, []);

  const clearStack = useCallback(() => {
    setStack([]);
  }, []);

  const setTabLocation = useCallback((tabId: TabId, location: TabLocation) => {
    setTabLocations((current) => {
      const existing = current[tabId];
      if (existing?.id === location.id) {
        return current;
      }
      return {
        ...current,
        [tabId]: location,
      };
    });
  }, []);

  const setMapView = useCallback((view: MapViewState | null) => {
    mapViewRef.current = view;
    persistMapView(view);
  }, []);

  const getMapView = useCallback(() => mapViewRef.current, []);

  const registerReselect = useCallback((tabId: TabId, handler: () => void) => {
    reselectHandlersRef.current[tabId].add(handler);
    return () => {
      reselectHandlersRef.current[tabId].delete(handler);
    };
  }, []);

  const emitReselect = useCallback((tabId: TabId) => {
    reselectHandlersRef.current[tabId].forEach((handler) => handler());
  }, []);

  const getTabPath = useCallback(
    (tabId: TabId) => {
      const location = tabLocations[tabId];
      if (!location) return TAB_ID_TO_PATH[tabId];
      return `${location.pathname}${location.search}${location.hash}`;
    },
    [tabLocations],
  );

  const getTabLocation = useCallback((tabId: TabId) => tabLocations[tabId], [tabLocations]);

  const value = useMemo<NavigationStackContextValue>(
    () => ({
      activeTab,
      stack,
      lastActiveTab,
      tabLocations,
      setActiveTab,
      pushStack,
      popStack,
      clearStack,
      setTabLocation,
      setMapView,
      getMapView,
      registerReselect,
      emitReselect,
      getTabPath,
      getTabLocation,
    }),
    [
      activeTab,
      stack,
      lastActiveTab,
      tabLocations,
      setActiveTab,
      pushStack,
      popStack,
      clearStack,
      setTabLocation,
      setMapView,
      getMapView,
      registerReselect,
      emitReselect,
      getTabPath,
      getTabLocation,
    ]
  );

  return (
    <NavigationStackContext.Provider value={value}>
      {children}
    </NavigationStackContext.Provider>
  );
}

// Hook
export function useNavigationStack() {
  const context = useContext(NavigationStackContext);
  if (!context) {
    throw new Error("useNavigationStack must be used within NavigationStackProvider");
  }
  return context;
}

// Safe hook that doesn't throw - use when context may not be available
export function useNavigationStackSafe() {
  return useContext(NavigationStackContext);
}

// Reselect hook (convenience wrapper)
export function useTabReselect(tabId: TabId, handler: () => void) {
  const { registerReselect } = useNavigationStack();
  // Using ref to avoid re-registering on handler change
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    return registerReselect(tabId, () => handlerRef.current());
  }, [registerReselect, tabId]);
}
